import { useEffect, useState } from 'react';
import { payrollGlobalApi as papi } from '../../../api/payrollGlobal.js';

/** Pay groups + their pay calendars, with create forms. */
export default function PayGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [g, c] = await Promise.all([papi.listPayGroups(), papi.listPayCalendars()]);
      setGroups(g?.items || []);
      setCalendars(c?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function submit(fn, fd, reset) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      reset?.();
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function createGroup(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const form = e.currentTarget;
    await submit(() => papi.createPayGroup({ name: fd.get('name'), jurisdictionCode: fd.get('jurisdictionCode'), currency: fd.get('currency'), schedule: fd.get('schedule') }), fd, () => form.reset());
  }
  async function createCalendar(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const form = e.currentTarget;
    await submit(() => papi.createPayCalendar({ payGroupId: fd.get('payGroupId'), periodStart: fd.get('periodStart'), periodEnd: fd.get('periodEnd'), payDate: fd.get('payDate') }), fd, () => form.reset());
  }

  return (
    <section data-testid="payroll-global-paygroups-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header"><div><h1>Pay Groups &amp; Calendars</h1><p className="muted">Populations paid together + their pay periods.</p></div></header>
      {error && <div className="alert error" data-testid="payroll-global-paygroups-error">{error}</div>}

      <form onSubmit={createGroup} className="card form-card grid-form">
        <label>Name<input name="name" data-testid="payroll-global-paygroup-name-input" required /></label>
        <label>Jurisdiction code<input name="jurisdictionCode" data-testid="payroll-global-paygroup-jurisdiction-input" placeholder="ID / US-CA / ZZ" required /></label>
        <label>Currency<input name="currency" data-testid="payroll-global-paygroup-currency-input" defaultValue="USD" /></label>
        <label>Schedule
          <select name="schedule" data-testid="payroll-global-paygroup-schedule-input" defaultValue="monthly">
            <option value="monthly">monthly</option><option value="biweekly">biweekly</option><option value="weekly">weekly</option>
          </select>
        </label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="payroll-global-paygroup-create" disabled={busy}>Add pay group</button></div>
      </form>

      <div className="card">
        <h3>Pay groups</h3>
        <table className="data-table" data-testid="payroll-global-paygroup-list">
          <thead><tr><th>Name</th><th>Jurisdiction</th><th>Currency</th><th>Schedule</th></tr></thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.id} data-testid={`payroll-global-paygroup-row-${g.id}`}>
                <td>{g.name}</td><td>{g.jurisdiction_code}</td><td>{g.currency}</td><td>{g.schedule}</td>
              </tr>
            ))}
            {!loading && groups.length === 0 && <tr><td colSpan={4} className="muted">No pay groups yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <form onSubmit={createCalendar} className="card form-card grid-form">
        <label>Pay group
          <select name="payGroupId" data-testid="payroll-global-calendar-group-input" required>
            <option value="">— select —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        <label>Period start<input name="periodStart" type="date" data-testid="payroll-global-calendar-start-input" required /></label>
        <label>Period end<input name="periodEnd" type="date" data-testid="payroll-global-calendar-end-input" required /></label>
        <label>Pay date<input name="payDate" type="date" data-testid="payroll-global-calendar-paydate-input" required /></label>
        <div className="full actions"><button className="btn primary" type="submit" data-testid="payroll-global-calendar-create" disabled={busy}>Add calendar</button></div>
      </form>

      <div className="card">
        <h3>Pay calendars</h3>
        <table className="data-table" data-testid="payroll-global-calendar-list">
          <thead><tr><th>Period</th><th>Pay date</th><th>Status</th></tr></thead>
          <tbody>
            {calendars.map((c) => (
              <tr key={c.id} data-testid={`payroll-global-calendar-row-${c.id}`}>
                <td>{String(c.period_start).slice(0, 10)} → {String(c.period_end).slice(0, 10)}</td>
                <td>{String(c.pay_date).slice(0, 10)}</td>
                <td>{c.status}</td>
              </tr>
            ))}
            {!loading && calendars.length === 0 && <tr><td colSpan={3} className="muted">No calendars yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
